import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit, Plus, Trash2, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BikesTab({ bikes, onRefresh, onDelete }) {
  const [isOpen, setIsOpen] = useState(false);
  const [editingBike, setEditingBike] = useState(null);
  const [formData, setFormData] = useState({ name: '', number: '', tuner: '', price: 25000, image_url: '', description: '', specs: '' });
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      setUploading(true);
      toast.loading('Uploading image...');
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.dismiss();
      
      const imageUrl = response.data.url;
      setFormData({ ...formData, image_url: imageUrl });
      toast.success('Image uploaded!');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBike) {
        await axios.put(`${BACKEND_URL}/api/moto/bikes/${editingBike.bike_id}`, formData, { withCredentials: true });
        toast.success('Bike updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/moto/bikes`, formData, { withCredentials: true });
        toast.success('Bike created');
      }
      setIsOpen(false);
      setEditingBike(null);
      setFormData({ name: '', number: '', tuner: '', price: 25000, image_url: '', description: '', specs: '' });
      onRefresh();
    } catch (error) {
      toast.error('Failed to save bike');
    }
  };

  const openEdit = (bike) => {
    setEditingBike(bike);
    setFormData({ name: bike.name, number: bike.number, tuner: bike.tuner, price: bike.price || 25000, image_url: bike.image_url || '', description: bike.description || '', specs: bike.specs || '' });
    setIsOpen(true);
  };

  return (
    <>
    <div className="bg-card border border-zinc-800 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading font-bold text-2xl">Moto <span className="text-secondary">Bikes</span> Management</h2>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="add-bike-button"
              onClick={() => { setEditingBike(null); setFormData({ name: '', number: '', tuner: '', price: 25000, image_url: '', description: '', specs: '' }); }}
              className="btn-skew bg-secondary text-black px-6 py-2 font-bold uppercase flex items-center space-x-2"
            >
              <Plus size={18} />
              <span>Add Bike</span>
            </button>
          </DialogTrigger>
          <DialogContent className="bg-card border border-zinc-800 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-2xl">
                {editingBike ? 'Edit Bike' : 'Add Bike'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Name</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary h-12 px-4 text-white" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Number</label>
                  <input type="number" value={formData.number} onChange={(e) => setFormData({ ...formData, number: parseInt(e.target.value) })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary h-12 px-4 text-white" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Tuner/Team</label>
                <input type="text" value={formData.tuner} onChange={(e) => setFormData({ ...formData, tuner: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary h-12 px-4 text-white" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Price (Credits: 15K-50K)</label>
                <input type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary h-12 px-4 text-white" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Image</label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input type="text" value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="Enter URL or upload file" className="flex-1 bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary h-12 px-4 text-white" />
                    <label className={`flex items-center gap-2 px-4 h-12 cursor-pointer transition-colors ${uploading ? 'bg-zinc-700' : 'bg-secondary hover:bg-secondary/80'} text-black`}>
                      <Upload size={18} />
                      <span>{uploading ? 'Uploading...' : 'Upload'}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
                    </label>
                  </div>
                  {formData.image_url && (
                    <div className="flex items-center gap-2">
                      <img src={formData.image_url} alt="Preview" className="w-16 h-16 object-cover border border-zinc-700" />
                      <button type="button" onClick={() => setFormData({ ...formData, image_url: '' })} className="text-red-500 hover:text-red-400 text-sm">Remove</button>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Description (optional)</label>
                <textarea data-testid="bike-description-input" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary px-4 py-3 text-white min-h-[80px]" placeholder="Brief description about the bike..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Specifications (optional)</label>
                <textarea data-testid="bike-specs-input" value={formData.specs} onChange={(e) => setFormData({ ...formData, specs: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary px-4 py-3 text-white min-h-[80px]" placeholder="Engine, horsepower, performance stats..." />
              </div>
              <button type="submit" className="w-full bg-secondary text-black py-3 font-bold uppercase">
                {editingBike ? 'Update' : 'Create'}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="text-left p-4 font-heading font-bold text-sm text-zinc-400">#</th>
              <th className="text-left p-4 font-heading font-bold text-sm text-zinc-400">NAME</th>
              <th className="text-left p-4 font-heading font-bold text-sm text-zinc-400">TUNER</th>
              <th className="text-left p-4 font-heading font-bold text-sm text-zinc-400">PRICE</th>
              <th className="text-right p-4 font-heading font-bold text-sm text-zinc-400">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {bikes.map((bike) => (
              <tr key={bike.bike_id} className="border-b border-zinc-800 hover:bg-zinc-900">
                <td className="p-4 font-mono font-bold">{bike.number}</td>
                <td className="p-4 font-bold">{bike.name}</td>
                <td className="p-4 text-zinc-400">{bike.tuner}</td>
                <td className="p-4 text-secondary font-mono">{(bike.price || 0).toLocaleString()} CR</td>
                <td className="p-4 text-right">
                  <button onClick={() => openEdit(bike)} className="text-secondary hover:text-white mr-3">
                    <Edit size={18} />
                  </button>
                  <button onClick={() => onDelete(bike.bike_id)} className="text-red-500 hover:text-red-400">
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </>
  );
}